import React, { Component } from 'react';
import { string, node, func, bool } from 'prop-types';
import { Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { trapModalFocus } from 'Src/common/utilities/a11y_util';

import './style.scss';

const FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class ThemeXModal extends Component {
  constructor(props) {
    super(props);
    this.triggerElement = null;
    this.focusTimer = null;
    this.guardedWrap = null;
    this.releaseFocusTrap = null;
    this.titleRef = React.createRef();
    this.contentRef = React.createRef();
    // Unique IDs for aria-labelledby — generated once per instance.
    this.headerTitleId = `themex-modal-ttl-hdr-${Math.random().toString(36).substr(2, 8)}`;
    this.contentTitleId = `themex-modal-ttl-cnt-${Math.random().toString(36).substr(2, 8)}`;
  }

  componentDidMount() {
    const { visible } = this.props;
    if (visible) {
      this.captureTriggerAndFocus();
    }
  }

  componentDidUpdate(prevProps) {
    const { visible } = this.props;
    if (visible && !prevProps.visible) {
      this.captureTriggerAndFocus();
    }
  }

  componentWillUnmount() {
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
      this.focusTimer = null;
    }
    this.unguardWrapShiftTab();
    this.removeFocusTrap();
    // Unmounted modals (e.g. GiftModal) never reach afterClose, so restore focus here, unless another modal took over.
    const trigger = this.triggerElement;
    this.triggerElement = null;
    const dialog = this.contentRef.current && this.contentRef.current.closest('.ant-modal-wrap');
    const { activeElement } = document;
    const focusWasHere =
      !activeElement || activeElement === document.body || (dialog && dialog.contains(activeElement));
    if (!focusWasHere || !trigger || typeof trigger.focus !== 'function') return;
    setTimeout(() => {
      const otherModalOpen = Array.from(document.querySelectorAll('.ant-modal-wrap')).some(
        (wrap) => wrap !== dialog && wrap.style.display !== 'none' && !wrap.contains(trigger),
      );
      const focusStillLost = !document.activeElement || document.activeElement === document.body;
      if (!otherModalOpen && focusStillLost && document.contains(trigger)) trigger.focus();
    }, 0);
  }

  handleModalCancel(e) {
    const { handleCancel } = this.props;
    if (handleCancel) {
      return handleCancel(e);
    }
    return undefined;
  }

  handleAfterClose() {
    this.removeFocusTrap();
    this.restoreTriggerFocus();
  }

  setupFocusTrap() {
    const anchor = this.contentRef.current;
    const dialog = anchor && anchor.closest('.ant-modal');
    if (!dialog) return;
    this.removeFocusTrap();
    this.releaseFocusTrap = trapModalFocus(dialog);
  }

  // The wrap has to keep initial focus: reading forward from it covers the custom header above the
  // title (vendor r16/r26). But rc-dialog's trap only redirects Tab when focus is on one of its
  // sentinels, which sit inside the wrap, so Shift+Tab from the wrap itself would leave the dialog.
  // Handing focus to the end sentinel first lets the browser's own Shift+Tab land on the last
  // control inside, the same way rc-dialog wraps focus.
  guardWrapShiftTab(dialogWrap) {
    if (this.guardedWrap === dialogWrap) {
      return;
    }
    this.unguardWrapShiftTab();
    this.handleWrapKeyDown = (e) => {
      if (e.key !== 'Tab' || !e.shiftKey || document.activeElement !== dialogWrap) {
        return;
      }
      const sentinels = dialogWrap.querySelectorAll('[role="document"] > [tabindex="0"][aria-hidden="true"]');
      const sentinelEnd = sentinels[sentinels.length - 1];
      if (sentinelEnd) {
        sentinelEnd.focus();
      }
    };
    dialogWrap.addEventListener('keydown', this.handleWrapKeyDown);
    this.guardedWrap = dialogWrap;
  }

  unguardWrapShiftTab() {
    if (this.guardedWrap) {
      this.guardedWrap.removeEventListener('keydown', this.handleWrapKeyDown);
      this.guardedWrap = null;
    }
  }

  removeFocusTrap() {
    if (this.releaseFocusTrap) {
      this.releaseFocusTrap();
      this.releaseFocusTrap = null;
    }
  }

  // Restore after the close animation finishes so Antd's dialog teardown does not override us
  // during the 300ms transition. Clearing triggerElement first makes this safe to call from both
  // the afterClose and the unmount path without focusing twice.
  restoreTriggerFocus() {
    const trigger = this.triggerElement;
    this.triggerElement = null;
    if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
      trigger.focus();
    }
  }

  captureTriggerAndFocus() {
    const { initialFocus } = this.props;
    this.triggerElement = document.activeElement;

    this.focusTimer = setTimeout(() => {
      this.focusTimer = null;
      this.setupFocusTrap();
      if (initialFocus === 'dialog' && this.contentRef.current) {
        const dialogWrap = this.contentRef.current.closest('[role="dialog"]');
        if (dialogWrap) {
          this.guardWrapShiftTab(dialogWrap);
          dialogWrap.focus();
          return;
        }
      }
      // Prefer focusing the title heading if present, otherwise the first focusable child
      if (this.titleRef.current) {
        this.titleRef.current.focus();
        return;
      }
      if (this.contentRef.current) {
        const firstFocusable = this.contentRef.current.querySelector(FOCUSABLE_SELECTORS);
        if (firstFocusable) firstFocusable.focus();
      }
    }, 100);
  }

  render() {
    const {
      modalTitle,
      titleAsHeading,
      graphic,
      Description,
      Footer,
      title,
      visible,
      maskClosable,
      closable,
      keyboard,
      wrapClassName,
      ariaLabel,
      ariaLabelledBy,
      width,
      header,
      closeIcon,
      children,
    } = this.props;
    // Plain-text titles become the focused <h2>; JSX titles (e.g. ModalHeader) may carry their own heading or buttons, so they stay in a <div>
    const isHeadingTitle =
      titleAsHeading !== null ? titleAsHeading : typeof modalTitle === 'string' || typeof modalTitle === 'number';

    // Build aria-labelledby: prefer modalTitle ID, then content title ID.
    let labelledById = ariaLabelledBy;
    if (!labelledById && modalTitle) {
      labelledById = this.headerTitleId;
    } else if (!labelledById && (title || Description)) {
      labelledById = this.contentTitleId;
    }

    return (
      <Modal
        width={width}
        title={
          modalTitle &&
          (isHeadingTitle ? (
            <h2 id={this.headerTitleId} className="arc-H200" tabIndex={-1} ref={this.titleRef}>
              {modalTitle}
            </h2>
          ) : (
            <div id={this.headerTitleId} className="arc-H200">
              {modalTitle}
            </div>
          ))
        }
        footer={null}
        visible={visible}
        maskClosable={maskClosable}
        closable={closable}
        keyboard={keyboard}
        onCancel={(e) => this.handleModalCancel(e)}
        afterClose={() => this.handleAfterClose()}
        className="themex-modal"
        wrapClassName={wrapClassName}
        wrapProps={{
          'aria-modal': 'true',
          'aria-labelledby': labelledById,
          'aria-label': !labelledById && ariaLabel ? ariaLabel : undefined,
        }}
        closeIcon={<div>{closeIcon || <FontAwesomeIcon style={{ fontSize: '20px' }} icon={faTimes} />}</div>}>
        <If condition={header}>
          <div className="themex-modal-header-content">{header}</div>
        </If>
        <If condition={graphic}>
          <div className="themex-modal-graphic-container">
            <img src={graphic} alt="" className="themex-modal-graphic" />
          </div>
        </If>
        <If condition={title || Description}>
          <div className="themex-modal-adjacent-graphic-content">
            <h2
              id={this.contentTitleId}
              className="arc-H300 arc-B85 themex-modal-title"
              tabIndex={-1}
              ref={this.titleRef}>
              {title}
            </h2>
            {Description}
          </div>
        </If>
        <div className="theme-modal-children-content" ref={this.contentRef}>
          {children}
        </div>
        <div className="theme-modal-footer">{Footer}</div>
      </Modal>
    );
  }
}

ThemeXModal.propTypes = {
  width: string,
  modalTitle: node,
  visible: bool,
  maskClosable: bool,
  closable: bool,
  keyboard: bool,
  graphic: string,
  wrapClassName: string,
  title: string,
  ariaLabel: string,
  initialFocus: string,
  ariaLabelledBy: string,
  titleAsHeading: bool,
  Description: node,
  Footer: node,
  header: node,
  closeIcon: node,
  children: node,
  handleCancel: func,
};

ThemeXModal.defaultProps = {
  width: '24rem',
  visible: false,
  maskClosable: true,
  closable: true,
  keyboard: true,
  graphic: null,
  title: '',
  wrapClassName: '',
  ariaLabel: null,
  initialFocus: 'title',
  ariaLabelledBy: null,
  modalTitle: null,
  titleAsHeading: null,
  Description: null,
  Footer: null,
  header: null,
  closeIcon: null,
  children: null,
  handleCancel: null,
};

export default ThemeXModal;
